/** 极小的 className 拼接工具，避免为了一个 cn() 引入 clsx/tailwind-merge。 */
import { formatElapsedClock } from '@fht/shared';

export function cn(...parts: Array<string | false | null | undefined | 0>): string {
  return parts.filter((part): part is string => typeof part === 'string' && part.length > 0).join(' ');
}

/** 手机号/邮箱等展示用的掩码（隐私最小化）。 */
export function maskEmail(email: string | null | undefined): string {
  if (!email) return '';
  const [local, domain] = email.split('@');
  if (!local || !domain) return email;
  const head = local.slice(0, 2);
  return `${head}${'*'.repeat(Math.max(1, local.length - 2))}@${domain}`;
}

/**
 * 把 ISO 时间格式化为 'MM-DD HH:mm'（**浏览器本地时区**，没有年份）。
 *
 * ⚠️ **已经没有任何生产调用点了**（2026-10 第二轮清理把它剩下的 7 处全部迁走），
 * 新代码一律别再用它。它**故意保留**在这里：
 *   · `lib/utils.ts` 是对外的公共 API，删掉可能影响别处或将来；
 *   · 而且 `lib/dateFormat.test.ts` 还引用它，把下面这两个毛病**记录下来**
 *     —— 留着"为什么不能这么写"的证据，比删掉它更有用。
 *
 * 两个老毛病：
 *   1. **没有年份** —— 跨年的记录看着像今年（用户 2026-10 指出「最后登录/创建时间
 *      没有年份」就是它）；
 *   2. **按浏览器时区渲染**，而不是家庭时区（见下面的 `FAMILY_TZ`）——同一个时间
 *      在服务器、家长手机、孩子平板上可能显示成不同的日期。
 *
 * 替代品按场合二选一（两者都走家庭时区，本函数是**唯一**按浏览器时区的那个）：
 *   · **表格 / 列表单元格、台账类时间戳** → `formatDateTimeFull`（'YYYY-MM-DD HH:mm'；
 *     账号台账、积分明细、共享邀请、登录设备、错词本、备份时间等）；
 *   · **嵌在句子里的短标签**（"布置于…""…截止"） → `formatDateTimeLocal`（'MM/DD HH:mm'）。
 */
export function formatDateTime(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/** 秒 → 'mm:ss'，用于倒计时按钮。 */
export function formatCountdown(seconds: number): string {
  const s = Math.max(0, Math.floor(seconds));
  return `${String(Math.floor(s / 60)).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}`;
}

// ---------------------------------------------------------------- 时间展示
//
// 统一用 `Intl.DateTimeFormat`，并且**显式指定家庭时区**（见方案 3.5）：
// 服务器时区是 Asia/Tokyo、家庭是 Asia/Shanghai，若用浏览器默认时区格式化，
// 孩子在 23:30 打的卡可能显示成第二天 —— 那种"数字解释不通"的体验最伤信任。

export const FAMILY_TZ = 'Asia/Shanghai';

const DATE_TIME_FMT = new Intl.DateTimeFormat('zh-CN', {
  timeZone: FAMILY_TZ,
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  hour12: false,
});

const DATE_FMT = new Intl.DateTimeFormat('en-CA', {
  timeZone: FAMILY_TZ,
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
});

/**
 * ISO 时间 → '03/07 14:05'（家庭时区）。
 *
 * ⚠️ 分隔符是 `/` 而不是 `-` —— `Intl.DateTimeFormat('zh-CN', …)` 的日期分隔符就是 `/`。
 * （`formatDateTimeFull` 为了拿到 ISO 风格的 `2026-03-07` 才改用 `formatToParts` 自己拼。）
 * 这里的注释在 2026-10 之前一直写的是 `'03-07 14:05'`，与实际输出不符，已更正为实话。
 */
export function formatDateTimeLocal(iso: string | null | undefined): string {
  if (!iso) return '—';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  return DATE_TIME_FMT.format(date);
}

/** ISO 时间 → 'YYYY-MM-DD'（家庭时区）。 */
export function formatDateLocal(iso: string | null | undefined): string {
  if (!iso) return '—';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  return DATE_FMT.format(date);
}

/**
 * ISO 时间 → 'YYYY-MM-DD HH:mm'（家庭时区）——**带年份**、跨年不会误解的那一套。
 *
 * 什么时候用它：凡是"账号台账"性质的时间（最后登录、创建时间、停用时间、审计日志
 * 时间、邀请码到期、备份成功时间），年份是判断新旧的必要信息，就该走这里。
 * 什么时候别用它：句子里嵌的"布置于 10/20 16:00"这类短标签，年份只会把行挤长 ——
 * 那些仍然用 `formatDateTimeLocal`（`'MM/DD HH:mm'`；注意分隔符是 `/`）。
 *
 * 拿不到值（`null` / `undefined` / 空串）时返回 `'—'`，**不吞成空白**，与
 * `formatDateLocal` / `formatDateTimeLocal` 同一口径。
 *
 * 用 `formatToParts` 自己拼，而不是直接 `format()` —— zh-CN 的默认日期分隔符是 `/`，
 * 拼出来的 '2026/10/12 21:35' 不是我们要的 ISO 风格 `2026-10-12 21:35`。
 * `hourCycle: 'h23'` 是为了避免个别环境把午夜格式化成 '24:00'。
 */
const FULL_DATE_TIME_FMT = new Intl.DateTimeFormat('zh-CN', {
  timeZone: FAMILY_TZ,
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  hourCycle: 'h23',
});

export function formatDateTimeFull(iso: string | null | undefined): string {
  if (!iso) return '—';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  const parts = FULL_DATE_TIME_FMT.formatToParts(date);
  const pick = (type: Intl.DateTimeFormatPartTypes) =>
    parts.find((part) => part.type === type)?.value ?? '';
  return `${pick('year')}-${pick('month')}-${pick('day')} ${pick('hour')}:${pick('minute')}`;
}

/**
 * 计时器显示用的时长：'12:34'（分:秒）或 '1:02:03'（时:分:秒）。
 *
 * ⚠️ 实现只有一份，在 `@fht/shared` 的 `timer-panel.ts`（`formatElapsedClock`）——
 * 计时面板（`components/ui/CheckinTimerPanel.tsx`）与"完成弹窗里的已用时"读的是
 * 同一个函数，否则同一项作业在两个地方会显示成两种格式。这里**转发**，
 * 保留这个名字是因为它已经在多处被引用（`StudentTodayPage` 的完成弹窗等）。
 *
 * 刻意不用 `@fht/shared` 的 `formatDuration`（那个给的是"19 分 23 秒"这种
 * **句子里嵌的叙述式用时**），计时器需要等宽数字、秒级跳动、位数稳定的格式。
 * 两者都保留秒，区别只在排版：一个给"读"，一个给"看着它走"。
 */
export function formatClock(seconds: number): string {
  return formatElapsedClock(seconds);
}

/**
 * 秒 → `'25 分钟'` / `'1 小时 5 分'` —— ⚠️ **只给「计划 / 目标时长」用**。
 *
 * 两个概念在这里必须分清楚（混淆它们是这个需求最容易犯的错）：
 *
 * - **计划时长**（`estimatedSeconds`、"每天阅读 30 分钟"这类**配置值**）：
 *   是"打算做多久"，孩子自己填的都是整分钟，显示到分钟就够 → **本函数**。
 * - **实际用时**（服务端的 `actualSeconds`，孩子真实投入）：→ ⛔ **必须**用
 *   `@fht/shared` 的 `formatDuration()`（`'45 秒'` / `'19 分 23 秒'`）。
 *
 * ⚠️ 本函数**四舍五入到分钟**，所以拿它显示实际用时会出事故：45 秒 → `'1 分钟'`，
 * 20 秒 → `'0 分钟'`（用户就是看到"用时 0 分钟"才以为应用没记录完成时间）。
 * 名字里的 `Planned` 就是为了让这个误用在下一次 code review 里一眼可见 ——
 * 它以前叫 `formatMinutesText`，那个名字谁都能顺手拿去格式化用时。
 */
export function formatPlannedDuration(seconds: number | null | undefined): string {
  if (!seconds || seconds <= 0) return '—';
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `${minutes} 分钟`;
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return m === 0 ? `${h} 小时` : `${h} 小时 ${m} 分`;
}

/** 数值 + 单位：'30 分钟' / '一次'。 */
export function formatValueUnit(value: number | null | undefined, unit: string | null | undefined): string {
  if (value === null || value === undefined) return '一次';
  return `${value}${unit ?? ''}`;
}

